import React, { useEffect, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import OutlinedInput from '@mui/material/OutlinedInput';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Typography from '@mui/material/Typography';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import KeyIcon from '@mui/icons-material/Key';
import MailIcon from '@mui/icons-material/Mail';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useDispatch, useSelector } from 'react-redux';
import Eex from './Eex';
import Limits from './Limits';
import ClientHardLimit from './ClientHardLimit';
import EexUserLimits from './EexUserLimits';
import {
    apiLoginEex,
    clearAuthError
} from '../../../store/feature/eex/eexSlice';

const LOCAL_EEX_WS_URL = process.env.REACT_APP_EEX_WS_URL;

const EEX_WS_URL =
    window.location.hostname === 'localhost'
        ? LOCAL_EEX_WS_URL
        : `wss://${window.location.host}/eexapi/ws/prices`;
const INITIAL_RECONNECT_DELAY_MS = 1000;
const MAX_RECONNECT_DELAY_MS = 5000;

const EVENT_TYPE_GROUPED_LIVE_ORDERS = 'grouped_live_orders';

const getNumericValue = (value, fallback = 0) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : fallback;
};

const buildEexWsUrl = (username, token) => {
    if (!EEX_WS_URL) {
        throw new Error('EEX websocket URL is not configured.');
    }

    const url = new URL(EEX_WS_URL);
    url.searchParams.set('username', username);
    url.searchParams.set('token', token);
    return url.toString();
};

const normalizeSnapshotToRows = (snapshot) => {
    const ordersByContract = snapshot?.orders_by_contract;

    if (!ordersByContract || typeof ordersByContract !== 'object') {
        return { rows: [], products: [] };
    }

    const productSet = new Set();
    Object.values(ordersByContract).forEach((productsMap) => {
        Object.keys(productsMap || {}).forEach((productName) => {
            productSet.add(productName);
        });
    });

    const encountered = Array.from(productSet);
    const preferredOrder = ['TTF', 'THE', 'PEG'];
    const products = [
        ...preferredOrder.filter((p) => encountered.includes(p)),
        ...encountered.filter((p) => !preferredOrder.includes(p))
    ];
    const rows = [];

    Object.entries(ordersByContract).forEach(([contractName, productsMap]) => {
        const perProductDepth = {};
        let maxDepth = 0;

        products.forEach((productName) => {
            const productObj = productsMap?.[productName] || {};
            const rawBids = Array.isArray(productObj?.bids)
                ? [...productObj.bids]
                : [];
            const rawAsks = Array.isArray(productObj?.asks)
                ? [...productObj.asks]
                : [];

            rawBids.sort(
                (a, b) =>
                    getNumericValue(b?.Price, 0) - getNumericValue(a?.Price, 0)
            );
            rawAsks.sort(
                (a, b) =>
                    getNumericValue(a?.Price, 0) - getNumericValue(b?.Price, 0)
            );

            perProductDepth[productName] = { bids: rawBids, asks: rawAsks };
            maxDepth = Math.max(maxDepth, rawBids.length, rawAsks.length);
        });

        for (let depth = 0; depth < maxDepth; depth += 1) {
            const row = { Contract: contractName };

            products.forEach((productName) => {
                const { bids, asks } = perProductDepth[productName] || {
                    bids: [],
                    asks: []
                };
                const bid = bids[depth] || null;
                const ask = asks[depth] || null;

                row[productName] = {
                    OrderId:
                        bid?.PersistentOrderId || ask?.PersistentOrderId || '-',
                    BidOrderId: bid?.PersistentOrderId || bid?.OrderId || '',
                    AskOrderId: ask?.PersistentOrderId || ask?.OrderId || '',
                    BidPersistentOrderId: bid?.PersistentOrderId || '',
                    AskPersistentOrderId: ask?.PersistentOrderId || '',
                    BidRawOrderId: bid?.OrderId || '',
                    AskRawOrderId: ask?.OrderId || '',
                    EngineId: bid?.EngineId || ask?.EngineId || '',
                    BrokerId: bid?.BrokerId ?? ask?.BrokerId ?? 0,
                    FirstSequenceId:
                        bid?.FirstSequenceId ?? ask?.FirstSequenceId ?? 0,
                    InstName: bid?.InstName || ask?.InstName || '',
                    RouteId: bid?.RouteId ?? ask?.RouteId ?? 0,
                    _rawBid: bid || null,
                    _rawAsk: ask || null,
                    EventCount: bid || ask ? depth + 1 : 0,
                    BidQty: bid
                        ? getNumericValue(bid?.Volume ?? bid?.Quantity, 0)
                        : 0,
                    AskQty: ask
                        ? getNumericValue(ask?.Volume ?? ask?.Quantity, 0)
                        : 0,
                    Bid: bid ? getNumericValue(bid?.Price, null) : null,
                    Ask: ask ? getNumericValue(ask?.Price, null) : null,
                    AllOrNone: !!(bid?.AllOrNone || ask?.AllOrNone),
                    Private: !!(
                        bid?.ManualOrderIndicator || ask?.ManualOrderIndicator
                    ),
                    Status: bid?.Status || ask?.Status || '',
                    ExpiryTime: bid?.ExpiryTime || ask?.ExpiryTime || '',
                    PriceDelta: getNumericValue(
                        bid?.PriceDelta ?? ask?.PriceDelta,
                        0
                    ),
                    Discretionary: getNumericValue(
                        bid?.Discretionary ?? ask?.Discretionary,
                        0
                    ),
                    UpdatedAt:
                        bid?.CreatedAt ||
                        ask?.CreatedAt ||
                        bid?.BroadcastTimestamp ||
                        ask?.BroadcastTimestamp ||
                        '',
                    IsOwnData: !!(bid?.IsOwnData || ask?.IsOwnData)
                };
            });

            rows.push(row);
        }
    });

    return { rows, products };
};

export default function EexTab() {
    const dispatch = useDispatch();
    const [value, setValue] = useState(0);
    const [formState, setFormState] = useState({
        username: '',
        password: '',
        showPassword: false
    });
    const [credentials, setCredentials] = useState(() => {
        const token = localStorage.getItem('eex_token');
        const username = localStorage.getItem('eex_username');
        return token && username ? { username } : null;
    });
    const [connectionRequest, setConnectionRequest] = useState(() => {
        const token = localStorage.getItem('eex_token');
        const username = localStorage.getItem('eex_username');
        return token && username ? { username } : null;
    });
    const [connectionState, setConnectionState] = useState('closed');
    const [error, setError] = useState('');
    const [rows, setRows] = useState([]);
    const [products, setProducts] = useState([]);
    const [loginError, setLoginError] = useState('');
    const [isLoggingIn, setIsLoggingIn] = useState(false);

    const wsRef = useRef(null);
    const reconnectTimeoutRef = useRef(null);
    const reconnectDelayRef = useRef(INITIAL_RECONNECT_DELAY_MS);
    const shouldReconnectRef = useRef(true);
    const authenticatedRef = useRef(false);

    const isAuthError = useSelector((state) => state.eex.isAuthError);

    useEffect(() => {
        if (!isAuthError) return;
        localStorage.removeItem('eex_token');
        localStorage.removeItem('eex_username');
        setCredentials(null);
        setConnectionRequest(null);
        shouldReconnectRef.current = false;
        if (wsRef.current) {
            wsRef.current.onclose = null;
            wsRef.current.onerror = null;
            wsRef.current.close();
            wsRef.current = null;
        }
        setConnectionState('closed');
        authenticatedRef.current = false;
        setLoginError('Session expired. Please log in again.');
        dispatch(clearAuthError());
    }, [isAuthError, dispatch]);

    const handleChange = (event, newValue) => {
        setValue(newValue);
    };

    const handleFormChange = (event) => {
        const { name, value } = event.target;
        setFormState((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        setLoginError('');
        setIsLoggingIn(true);
        setCredentials(null);

        try {
            const loginResponse = await dispatch(
                apiLoginEex({
                    username: formState.username,
                    password: formState.password
                })
            ).unwrap();

            const token =
                loginResponse?.data?.token ||
                loginResponse?.data?.access_token ||
                loginResponse?.token;

            if (!token) {
                throw new Error('Login response did not include a token.');
            }

            localStorage.setItem('eex_token', token);
            localStorage.setItem('eex_username', formState.username.trim());
            setFormState((prev) => ({
                ...prev,
                password: '',
                showPassword: false
            }));
            setConnectionRequest({
                username: formState.username.trim()
            });
        } catch (loginError) {
            const message =
                loginError?.message ||
                loginError?.data?.message ||
                loginError?.data?.detail ||
                loginError?.error ||
                'Unable to log in to EEX.';

            setLoginError(message);
            setConnectionRequest(null);
            setCredentials(null);
        } finally {
            setIsLoggingIn(false);
        }
    };

    useEffect(() => {
        if (!connectionRequest?.username) {
            setConnectionState('closed');
            setError('');
            authenticatedRef.current = false;
            return undefined;
        }

        shouldReconnectRef.current = true;
        authenticatedRef.current = false;

        const clearReconnectTimeout = () => {
            if (reconnectTimeoutRef.current) {
                clearTimeout(reconnectTimeoutRef.current);
                reconnectTimeoutRef.current = null;
            }
        };

        const closeSocket = () => {
            if (wsRef.current) {
                wsRef.current.onclose = null;
                wsRef.current.onerror = null;
                wsRef.current.close();
                wsRef.current = null;
            }
        };

        const scheduleReconnect = () => {
            setRows([]);
            setProducts([]);
            if (!shouldReconnectRef.current) return;

            setConnectionState('reconnecting');
            clearReconnectTimeout();

            const delay = reconnectDelayRef.current;
            reconnectTimeoutRef.current = setTimeout(() => {
                connect();
            }, delay);

            reconnectDelayRef.current = Math.min(
                reconnectDelayRef.current * 2,
                MAX_RECONNECT_DELAY_MS
            );
        };

        const connect = () => {
            clearReconnectTimeout();
            closeSocket();
            setError('');
            setConnectionState((prev) =>
                prev === 'reconnecting' ? 'reconnecting' : 'connecting'
            );

            try {
                const token = localStorage.getItem('eex_token');

                if (!token) {
                    setLoginError('Missing EEX token. Please log in again.');
                    setConnectionRequest(null);
                    setCredentials(null);
                    return;
                }

                const ws = new WebSocket(
                    buildEexWsUrl(connectionRequest.username, token)
                );

                ws.onopen = () => {
                    setConnectionState('connected');
                    reconnectDelayRef.current = INITIAL_RECONNECT_DELAY_MS;
                    setError('');
                    setLoginError('');
                    authenticatedRef.current = true;
                    setCredentials((current) => current || connectionRequest);
                };

                ws.onmessage = (event) => {
                    try {
                        const parsed = JSON.parse(event.data);

                        if (
                            (parsed?.event_type || '').toString() !==
                            EVENT_TYPE_GROUPED_LIVE_ORDERS
                        ) {
                            return;
                        }

                        const { rows: nextRows, products: nextProducts } =
                            normalizeSnapshotToRows(parsed);

                        setRows(nextRows);
                        setProducts(nextProducts);
                    } catch {
                        // Ignore malformed frames.
                    }
                };

                ws.onerror = () => {
                    const wasAuthenticated = authenticatedRef.current;
                    setConnectionState('closed');
                    authenticatedRef.current = false;
                    setConnectionRequest(null);
                    setCredentials(null);

                    if (!wasAuthenticated) {
                        localStorage.removeItem('eex_token');
                        localStorage.removeItem('eex_username');
                        setLoginError('Invalid EEX username or password.');
                    } else {
                        setError(
                            'WebSocket error occurred while streaming EEX prices.'
                        );
                    }
                };

                ws.onclose = () => {
                    const wasAuthenticated = authenticatedRef.current;
                    setConnectionState('closed');
                    authenticatedRef.current = false;
                    setConnectionRequest(null);
                    setCredentials(null);

                    if (!wasAuthenticated) {
                        localStorage.removeItem('eex_token');
                        localStorage.removeItem('eex_username');
                        setLoginError('Invalid EEX username or password.');
                    } else {
                        setError(
                            'WebSocket connection closed. Please log in again.'
                        );
                    }
                };

                wsRef.current = ws;
            } catch {
                setError(
                    'Unable to create WebSocket connection for EEX prices.'
                );
                scheduleReconnect();
            }
        };

        connect();

        return () => {
            shouldReconnectRef.current = false;
            clearReconnectTimeout();
            closeSocket();
        };
    }, [connectionRequest]);

    const handleOrderUpdated = (tradeEntry, payload) => {
        const productName = tradeEntry?.productName;
        const contractName = tradeEntry?.row?.Contract;

        if (!productName || !contractName) {
            console.warn(
                '[EEX] handleOrderUpdated: missing productName or contractName'
            );
            return;
        }

        const isBid = payload.side === 'BID';

        setRows((prevRows) => {
            const updated = prevRows.map((row) => {
                if (row.Contract !== contractName) return row;

                const productData = row[productName];
                if (!productData) return row;

                const patch = isBid
                    ? {
                          Bid: payload.price,
                          BidQty: payload.volume,
                          AllOrNone: payload.all_or_none
                      }
                    : {
                          Ask: payload.price,
                          AskQty: payload.volume,
                          AllOrNone: payload.all_or_none
                      };

                return { ...row, [productName]: { ...productData, ...patch } };
            });

            return updated;
        });
    };

    if (!credentials) {
        return (
            <Box sx={styles.loginRoot}>
                <Box
                    sx={styles.loginCard}
                    component="form"
                    onSubmit={handleSubmit}
                >
                    <Box sx={styles.loginIcon}>
                        <LockOutlinedIcon sx={{ fontSize: '30px' }} />
                    </Box>
                    <Typography
                        component="h1"
                        variant="h5"
                        sx={{ fontWeight: 700 }}
                    >
                        EEX Login
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 1 }}
                    >
                        Enter your EEX username and password to unlock the
                        market screens.
                    </Typography>

                    {loginError && (
                        <Box sx={{ mt: 2, width: '100%' }}>
                            <Typography color="error" variant="body2">
                                {loginError}
                            </Typography>
                        </Box>
                    )}

                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel htmlFor="eex-login-username">
                                    Username
                                </InputLabel>
                                <OutlinedInput
                                    label="Username"
                                    value={formState.username}
                                    name="username"
                                    autoComplete="username"
                                    onChange={handleFormChange}
                                    id="eex-login-username"
                                    required
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <MailIcon />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth variant="outlined">
                                <InputLabel htmlFor="eex-login-password">
                                    Password
                                </InputLabel>
                                <OutlinedInput
                                    label="Password"
                                    value={formState.password}
                                    autoComplete="current-password"
                                    onChange={handleFormChange}
                                    name="password"
                                    type={
                                        formState.showPassword
                                            ? 'text'
                                            : 'password'
                                    }
                                    id="eex-login-password"
                                    required
                                    endAdornment={
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                edge="end"
                                                onClick={() =>
                                                    setFormState((prev) => ({
                                                        ...prev,
                                                        showPassword:
                                                            !prev.showPassword
                                                    }))
                                                }
                                            >
                                                {formState.showPassword ? (
                                                    <VisibilityOff />
                                                ) : (
                                                    <Visibility />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    }
                                    startAdornment={
                                        <InputAdornment position="start">
                                            <KeyIcon />
                                        </InputAdornment>
                                    }
                                />
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        sx={styles.loginButton}
                        disabled={isLoggingIn || !!connectionRequest}
                    >
                        {isLoggingIn || connectionRequest
                            ? 'Connecting...'
                            : 'Log In'}
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box sx={{ width: '100%' }}>
            <Box
                sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    display: 'flex',
                    justifyContent: 'space-between'
                }}
            >
                <Tabs
                    value={value}
                    onChange={handleChange}
                    aria-label="EEX navigation tabs"
                    role="navigation"
                    sx={{
                        background: '#fff',
                        color: 'white',
                        borderRadius: '5px',
                        padding: '5px',
                        margin: '0 0 10px 10px',
                        boxShadow: '0 0 5px rgba(0, 0, 0, 0.1)',
                        minHeight: '45px',
                        '& .MuiTabs-indicator': {
                            display: 'none'
                        },
                        '& .MuiTab-root': {
                            minHeight: '35px',
                            height: '35px',
                            '&.Mui-selected': {
                                color: '#3AACFE',
                                background: '#DFF2FF',
                                borderRadius: '5px',
                                borderBottom: 'none'
                            }
                        }
                    }}
                >
                    <Tab label="Prices" aria-label="View EEX prices" />
                    <Tab label="Limits" aria-label="View EEX limits" />

                    <Tab
                        label="EEX USER LIMITS"
                        aria-label="View EEX user limits"
                    />
                </Tabs>
            </Box>
            <Box sx={{ padding: '10px' }}>
                {value === 0 && (
                    <Eex
                        rows={rows}
                        products={products}
                        connectionState={connectionState}
                        error={error}
                        onOrderUpdated={handleOrderUpdated}
                    />
                )}
                {value === 1 && <Limits />}
                {value === 2 && <EexUserLimits />}
            </Box>
        </Box>
    );
}

const styles = {
    loginRoot: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 'calc(100vh - 140px)',
        px: 2,
        py: 4,
        background:
            'radial-gradient(circle at top, rgba(60, 195, 193, 0.14), transparent 42%), linear-gradient(180deg, #f7fbfc 0%, #edf5f8 100%)'
    },
    loginCard: {
        width: '100%',
        maxWidth: '420px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        borderRadius: '18px',
        px: 4,
        py: 4,
        backgroundColor: '#fff',
        boxShadow: '0 20px 50px rgba(18, 38, 63, 0.12)',
        border: '1px solid rgba(36, 83, 114, 0.08)'
    },
    loginIcon: {
        color: '#fff',
        backgroundColor: '#245372',
        borderRadius: '50%',
        width: '56px',
        height: '56px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        mb: 0.5
    },
    loginButton: {
        fontWeight: 700,
        mt: 2,
        height: '48px',
        backgroundColor: '#245372',
        '&:active': {
            backgroundColor: '#1a3d54'
        },
        '&:hover': {
            backgroundColor: '#1a3d54'
        }
    }
};
